import { Suspense, useEffect, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'
import { AccountMenu } from '@/components/auth/account-menu'
import { Logo } from '@/components/brand/logo'
import { Icon, type IconName } from '@/components/icons/icon'
import { ThemeToggle } from '@/components/theme/theme-toggle'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useDocumentTitle } from '@/hooks/use-document-title'

export interface NavItem {
  to: string
  label: string
  icon: IconName
  end?: boolean
}

interface AppShellProps {
  /** Names the console, e.g. "Dashboard" or "Platform console". */
  area: string
  /** The first item is the console's home. */
  nav: NavItem[]
  /** Shown in the top bar on every page of the console, e.g. who it is for. */
  marker?: ReactNode
}

function isActive(pathname: string, item: NavItem) {
  return item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`)
}

/** Shared chrome for the tenant dashboard and the platform console. */
export function AppShell({ area, nav, marker }: AppShellProps) {
  const { pathname } = useLocation()
  const home = nav[0]
  useDocumentTitle((nav.find((item) => isActive(pathname, item)) ?? home).label, area)

  return (
    <TooltipProvider>
      <SidebarProvider>
        <div aria-hidden="true" className="duo-line pointer-events-none fixed inset-x-0 top-0 z-50" />
        <Sidebar collapsible="icon">
          <SidebarHeader className="h-16 shrink-0 flex-row items-center border-b border-sidebar-border px-4 pt-[3px] group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2">
            <Link
              to={home.to}
              className="flex items-center rounded-[9px] outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            >
              <span className="group-data-[collapsible=icon]:hidden">
                <Logo className="h-[30px]" />
              </span>
              <span className="hidden group-data-[collapsible=icon]:block">
                <Logo variant="mark" className="size-7" />
              </span>
            </Link>
          </SidebarHeader>

          <SidebarContent className="py-2">
            <SidebarGroup>
              <SidebarGroupLabel className="px-3 text-caption font-semibold text-faint">{area}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-1">
                  {nav.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        isActive={isActive(pathname, item)}
                        tooltip={item.label}
                        render={<NavLink to={item.to} end={item.end} />}
                        className="h-11 gap-3 rounded-[9px] px-3 text-ui text-muted-foreground hover:text-foreground data-active:font-semibold data-active:text-foreground data-active:[&_svg]:text-link md:h-10"
                      >
                        <Icon name={item.icon} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-sidebar-border p-2">
            <AccountMenu />
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>

        <SidebarInset className="min-w-0">
          <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 border-b border-border bg-background/88 px-4 pt-[3px] backdrop-blur-[14px] sm:px-6 lg:px-8">
            <SidebarTrigger
              aria-label="Toggle navigation"
              className="size-11 rounded-[9px] border border-border bg-card text-muted-foreground hover:bg-card hover:text-foreground dark:hover:bg-card md:size-[38px]"
            />
            <Crumbs area={area} nav={nav} pathname={pathname} />
            <div className="ml-auto flex items-center gap-2.5">
              {marker}
              <ThemeToggle />
            </div>
          </header>
          <div className="mx-auto w-full max-w-[1180px] flex-1 px-4 py-7 sm:px-6 md:py-10 lg:px-8">
            <Suspense key={pathname} fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </SidebarInset>
        <CloseMobileNavOnRouteChange pathname={pathname} />
      </SidebarProvider>
    </TooltipProvider>
  )
}

function Crumbs({ area, nav, pathname }: { area: string; nav: NavItem[]; pathname: string }) {
  const home = nav[0]
  const current = nav.find((item) => isActive(pathname, item)) ?? home
  return (
    <Breadcrumb className="min-w-0 flex-1">
      <BreadcrumbList className="flex-nowrap text-small">
        <BreadcrumbItem className="hidden sm:inline-flex">
          <BreadcrumbLink render={<Link to={home.to} />} className="truncate">
            {area}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="hidden sm:block" />
        <BreadcrumbItem className="min-w-0">
          <BreadcrumbPage className="truncate font-semibold">{current.label}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  )
}

/** On a phone the sidebar is a sheet; choosing a page should put it away. */
function CloseMobileNavOnRouteChange({ pathname }: { pathname: string }) {
  const { setOpenMobile } = useSidebar()
  useEffect(() => {
    setOpenMobile(false)
  }, [pathname, setOpenMobile])
  return null
}

export function PageSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <div className="space-y-3">
        <Skeleton className="h-8 w-48 rounded-[9px]" />
        <Skeleton className="h-4 w-80 max-w-full rounded-[9px]" />
      </div>
      <Skeleton className="h-56 w-full rounded-3xl" />
    </div>
  )
}
